import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const envValue = (name, fallback) => {
  try {
    return import.meta.env?.[name] || fallback;
  } catch {
    return fallback;
  }
};

const S3_BASE_URL = envValue(
  'VITE_S3_API_URL',
  'https://api.airaops.com',
).replace(/\/$/, '');

export const S3_UPLOAD_ENDPOINT = envValue(
  'VITE_S3_UPLOAD_ENDPOINT',
  '/api/v1/files/upload',
);
export const S3_SIGNED_URL_ENDPOINT = envValue(
  'VITE_S3_SIGNED_URL_ENDPOINT',
  '/api/v1/files/signed-url',
);

const s3Url = (endpoint) => (
  /^https?:\/\//i.test(endpoint) ? endpoint : `${S3_BASE_URL}${endpoint}`
);

const getUploadFileName = (file) => {
  const original = String(file?.name || file?.fileName || 'file').split(/[/\\]/).pop() || 'file';
  return original.replace(/[^\w.-]+/g, '_') || 'file';
};

const rejectUpload = (error, fallback) => {
  const responseData = error?.response?.data;
  const fileError = responseData?.error?.details?.errors?.file;
  if (fileError) return fileError;
  const responseMessage = responseData?.message;
  if (responseMessage) return responseMessage;
  if (error?.response?.status === 404) {
    return `S3 upload endpoint was not found: ${S3_UPLOAD_ENDPOINT}`;
  }
  return error?.response?.data?.error ?? error?.message ?? fallback;
};

export const s3FileUpload = createAsyncThunk(
  's3/fileUpload',
  async ({ file, folder }, { rejectWithValue }) => {
    try {
      const payload = {
        fileSize: file.size,
        subDirectory: folder,
        fileName: getUploadFileName(file),
      };
      const backendResponse = await api.post(s3Url(S3_UPLOAD_ENDPOINT), payload);
      const signedUrl = backendResponse.data.uploadSignedUrl;

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        return rejectWithValue('Error uploading file');
      }

      return backendResponse.data;
    } catch (error) {
      return rejectWithValue(rejectUpload(error, 'Error uploading file'));
    }
  },
);

export const getSignedUrl = createAsyncThunk(
  's3/getSignedUrl',
  async ({ params }, { rejectWithValue }) => {
    try {
      const response = await api.get(s3Url(S3_SIGNED_URL_ENDPOINT), { params });
      const body = response.data ?? {};
      const data = body.data ?? body;
      return data.signedUrl || data.uploadSignedUrl || '';
    } catch (error) {
      return rejectWithValue(rejectUpload(error, 'Error getting file signedUrl'));
    }
  },
);
